<template>
    <div
        ref="tooltipContainer"
        class="relative inline-block"
        @mouseenter="show"
        @mouseleave="hide"
    >
        <div ref="trigger">
            <slot></slot>
        </div>

        <transition
            enter-active-class="transition ease-out duration-150"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="isVisible"
                ref="tooltip"
                :class="[
                    'fixed z-50 rounded-lg max-w-90vw md:max-w-sm bg-theme-canvas shadow-md pointer-events-none',
                    border ? 'border border-theme-border' : ''
                ]"
                :style="tooltipStyle"
            >
                <div
                    class="absolute w-3 h-3 rotate-45 bg-theme-canvas"
                    :style="arrowStyle"
                ></div>

                <div
                    class="relative z-10 px-3 py-2 whitespace-normal text-sm w-max"
                >
                    <slot name="tooltip"></slot>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "CustomTooltip",
    props: {
        value: { type: Boolean, default: null },
        bottom: { type: Boolean, default: false },
        border: { type: Boolean, default: false },
        offset: { type: Number, default: 12 }
    },
    data() {
        return {
            isVisible: !!this.value,
            tooltipStyle: {},
            arrowStyle: {}
        };
    },
    watch: {
        value(newVal) {
            if (newVal === null) return;
            if (newVal) this.show();
            else this.hide();
        },
        isVisible(newVal) {
            this.$emit("input", newVal);
        }
    },
    mounted() {
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("scroll", this.handleResize, true);
        this.detachTooltip();
    },
    methods: {
        handleResize() {
            if (this.isVisible) this.calculatePosition();
        },
        detachTooltip() {
            const tooltip = this.$refs.tooltip;
            if (tooltip && tooltip.parentNode) {
                tooltip.parentNode.removeChild(tooltip);
            }
        },
        show() {
            this.isVisible = true;
            this.$nextTick(() => {
                const tooltip = this.$refs.tooltip;
                if (tooltip && tooltip.parentNode !== document.body) {
                    document.body.appendChild(tooltip);
                }
                this.calculatePosition();
                window.addEventListener("scroll", this.handleResize, true);
            });
        },
        hide() {
            this.isVisible = false;
            window.removeEventListener("scroll", this.handleResize, true);
        },
        calculatePosition() {
            const trigger = this.$refs.trigger;
            const tooltip = this.$refs.tooltip;
            if (!trigger || !tooltip) return;

            const triggerRect = trigger.getBoundingClientRect();
            const tWidth = triggerRect.width;
            const tooltipWidth = tooltip.offsetWidth;
            const tooltipHeight = tooltip.offsetHeight;

            let top = 0;
            const left = triggerRect.left + tWidth / 2 - tooltipWidth / 2;
            let arrowPos = {};

            if (this.bottom) {
                top = triggerRect.bottom + this.offset;
                arrowPos = {
                    top: "-6px",
                    left: "50%",
                    transform: "translateX(-50%) rotate(45deg)",
                    borderLeft: "1px solid currentColor",
                    borderTop: "1px solid currentColor"
                };
            } else {
                top = triggerRect.top - tooltipHeight - this.offset;
                arrowPos = {
                    bottom: "-6px",
                    left: "50%",
                    transform: "translateX(-50%) rotate(45deg)",
                    borderRight: "1px solid currentColor",
                    borderBottom: "1px solid currentColor"
                };
            }

            // Clamping lateral so the tooltip never overflows the viewport
            const padding = 12;
            const leftOnScreen = left;
            const rightOnScreen = leftOnScreen + tooltipWidth;

            let correctionX = 0;
            if (leftOnScreen < padding) correctionX = padding - leftOnScreen;
            else if (rightOnScreen > window.innerWidth - padding)
                correctionX = window.innerWidth - padding - rightOnScreen;

            this.tooltipStyle = {
                top: `${top}px`,
                left: `${left + correctionX}px`
            };
            this.arrowStyle = {
                ...arrowPos,
                left: `calc(50% - ${correctionX}px)`,
                borderColor: "#f3f4f6"
            };
        }
    }
};
</script>
